import OpenApiParser from '@apidevtools/swagger-parser';
import { Constants as MoltenConstants } from '@leverege/molten';
import { Relationships, AttributeCondition } from '@leverege/ui-attributes';
import { Constants } from '@leverege/api-redux';
import { GlobalState } from '@leverege/ui-redux';
import { Match, Plugins } from '@leverege/plugin';
import { ConditionCreators } from '@leverege/ui-condition';
import JWT from 'jsonwebtoken';
import OpenApiBlueprint from "../blueprint/OpenApiBlueprint.js";
import OpenApiDataSource from "../dataSource/OpenApiDataSource.js";
import Relationship from "../relationship/OpenApiRelationship.js";
import CreateAction from "../actions/CreateAction.js";
import UpdateAction from "../actions/UpdateAction.js";
import DeleteAction from "../actions/DeleteAction.js";
import { getObjectType } from "../util/OpenApiTypeUtil.js";
import AccessCreator from "../routes/access/AccessCreator.js";
import ItemInfoCreator from "../routes/itemInfo/ItemInfoCreator.js";
import ItemCreator from "../routes/item/ItemCreator.js";
import GroupCreator from "../routes/group/GroupCreator.js";
import { routeIsItem, routeIsList, resolveSchema } from "../util/OpenApiAnalyzerUtil.js";
import OpenApiCreator from "../openApiPlugin/OpenApiCreator.js";
import OpenApiGroupDelegate from "../delegates/openApi/OpenApiGroupDelegate.js";
import Api from "./Api.js";
import UserSettingsInit from "./UserSettingsInit.js";
function parsePath(urlPath, cxt, forApi) {
  let toReturn = urlPath.replace(/\{([a-zA-z0-9]*)\}/g, ':$1');
  if (cxt?.options?.basePath && !forApi) {
    toReturn = toReturn.slice(cxt?.options?.basePath.length);
  }
  return toReturn;
}
function parseRefPath(urlPath, cxt) {
  let p = parsePath(urlPath, cxt, false);
  if (p.match(/\/:[a-zA-Z0-9]*$/)) {
    p = p.split('/').slice(0, -1).join('/');
  }
  return p;
}
function parseRelPath(urlPath, cxt) {
  let toReturn = urlPath;
  if (cxt?.options?.basePath) {
    toReturn = toReturn.slice(cxt?.options?.basePath.length);
  }
  toReturn = toReturn.replace(/\/\{([a-zA-z0-9]*)\}/g, '');
  toReturn = toReturn.replace(/\//g, '.');
  return toReturn;
}
function getRelationshipOptions(route, cxt) {
  const {
    options,
    api
  } = cxt;
  // we only do json for now
  const getSuccessRes = route.methods?.get?.responses?.[200]?.content?.['application/json']?.schema;
  const resolvedSchema = resolveSchema(getSuccessRes);
  let objectType,
    itemsKey = null,
    isGroup = false,
    idKey;
  if (routeIsList(route, cxt)) {
    let items;
    itemsKey = options.listKey ?? route['x-lev-list-key'];
    if (itemsKey) {
      items = [itemsKey, resolvedSchema?.properties[itemsKey]];
    } else {
      items = Object.entries(resolvedSchema?.properties).find(([key, val]) => val?.type === 'array' && val?.items['x-lev-object-type']);
      itemsKey = items?.[0];
    }
    objectType = getObjectType(cxt.apiName, items[1].items['x-lev-object-type']);
    idKey = `${items[1].items['x-lev-object-type']}Id`;
    isGroup = true;
  } else if (routeIsItem(route, cxt)) {
    objectType = getObjectType(cxt.apiName, resolvedSchema['x-lev-object-type']);
    idKey = `${resolvedSchema['x-lev-object-type']}Id`;
  } else {
    return null;
  }
  const limitKey = route['x-lev-limit-key'] ?? options?.limitKey ?? 'limit';
  const offsetKey = route['x-lev-offset-key'] ?? options?.offsetKey ?? 'offset';
  const totalKey = route['x-lev-total-key'] ?? options?.totalKey ?? 'count';
  const sortKey = route['x-lev-sort-key'] ?? options?.sortKey ?? 'sort';
  const filterKey = route['x-lev-filter-key'] ?? options?.filterKey ?? 'filter';
  const path = parseRelPath(route.path, cxt);
  return {
    isGroup,
    apiName: cxt.apiName,
    api,
    objectType,
    idKey,
    blueprint: cxt.blueprints[objectType],
    limitKey,
    offsetKey,
    totalKey,
    itemsKey,
    sortKey,
    filterKey,
    path,
    urlPath: parsePath(route.path, cxt, false),
    refPath: parseRefPath(route.path, cxt),
    apiPath: parsePath(route.path, cxt, true),
    // TODO : placeholder
    get parent() {
      const parentPath = path.split('.').slice(0, -1).join('.');
      return Relationships.getByPath(parentPath);
    },
    get parentRelationship() {
      const parentPath = path.split('.').slice(0, -1).join('.');
      return Relationships.getByPath(parentPath);
    },
    relationshipType: parseRelPath(route.path, cxt).includes('/') ? 'oneToMany' : null,
    tags: route.methods?.['x-lev-tags'],
    bulkCreate: route.methods?.['x-lev-bulk-create'] === true,
    hasUserManagement: route.methods?.['x-lev-has-user-mgmt'],
    chain: []
  };
}
function processRoute(route, cxt) {
  const opts = getRelationshipOptions(route, cxt);
  if (opts) {
    const rel = new Relationship(opts, {
      match: {}
    });
    if (rel.isGroup) {
      Relationships.add(rel);
    }
    return rel;
  }
  return null;
}
function processRelationship(relationship, cxt) {
  const {
    molten
  } = cxt;
  if (relationship.isGroup && !relationship.parent) {
    GroupCreator(molten, {
      relationship,
      isRoot: true
    });
  } else if (relationship.isGroup) {
    GroupCreator(molten, {
      relationship
    });
  } else {
    ItemCreator(molten, {
      relationship
    });
    ItemInfoCreator(molten, {
      relationship
    });
    if (relationship.options.hasUserManagement) {
      AccessCreator(molten, {
        relationship
      });
    }
  }
}
function processApi(oApi, {
  molten
}) {
  const apiName = oApi['x-lev-options'].apiName || oApi.info?.title?.replace(/ /g, '') || 'default';
  const match = {
    apiName
  };
  const mutators = Plugins.get('OpenApiMutator', plg => {
    return Match.isMatch(match, plg.matches);
  }).sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  let api = oApi;
  for (let i = 0; i < mutators.length; i++) {
    api = mutators[i].mutate(api, {
      molten,
      apiName
    });
  }
  const schemas = api.components.schemas;
  const plg = OpenApiCreator({
    apiName,
    api
  });
  molten.addPlugin('OpenApi', plg);
  if (plg.options.useOwnAuth) {
    molten.addPlugin(MoltenConstants.PRERENDER, {
      id: `openApi.${apiName}.AuthInit`,
      onPrerender: async molten => {
        const token = localStorage.getItem(`${apiName}.id_token`);
        let profile;
        try {
          profile = JSON.parse(localStorage.getItem(`${apiName}.profile`));
        } catch (ex) {
          console.log('failed to parse profile', profile);
        }
        if (token) {
          const decoded = JWT.decode(token);
          if (decoded && decoded.exp * 1000 > Date.now()) {
            plg.comms.setToken(token);
            GlobalState.dispatch({
              type: Constants.AUTH_LOGGED_IN,
              profile: {
                ...profile,
                token
              },
              idToken: token
            });
          }
        }
      }
    });
    molten.addPlugin(MoltenConstants.LOGGED_OUT, {
      id: `openApi.${apiName}.AuthOnLogout`,
      sort: -10,
      onLoggedOut: molten => {
        localStorage.removeItem(`${apiName}.id_token`);
      }
    });
    console.warn('be sure to add molten.ApiUserSettings to excludes list');
    molten.addPlugin(Constants.PROVIDER, UserSettingsInit);
  } else if (plg.options.useImagineAuth) {
    molten.addPlugin(MoltenConstants.LOGGED_IN, {
      id: `openApi.${apiName}.AuthOnLogin`,
      sort: 100,
      onLoggedIn: (molten, action) => {
        const {
          idToken
        } = action;
        plg.comms.setToken(idToken);
      }
    });
  }
  if (plg.options.controlAuth) {
    console.warn('be sure to add molten.ApiInit to excludes list');
    molten.set('api', plg);
    molten.setThunkArg('api', plg);
    molten.addPlugin(Constants.PROVIDER, Api);
    molten.addPlugin(Constants.REDUX_REDUCER, Api);
  }
  const blueprints = {};
  const context = {
    blueprints,
    options: plg.options,
    apiName,
    molten,
    api: plg
  };
  if (schemas) {
    Object.values(schemas).filter(schema => schema['x-lev-object-type'] != null).forEach(bpModel => {
      const bp = new OpenApiBlueprint(bpModel, context);
      bp.installAttributes(molten);
      blueprints[bp.type] = bp;
    });
  }
  Object.values(blueprints).forEach(bp => {
    molten.addPlugin('DataSource', {
      type: bp.type,
      dataSource: new OpenApiDataSource({
        bp
      })
    });
    // TODO: move out to other file
    const conditionOptions = {},
      creators = ConditionCreators.toCreators([AttributeCondition]);
    molten.addPlugin('ConditionFilterOptionsCreator', {
      delegateType: OpenApiGroupDelegate.TYPE,
      objectType: bp.type,
      create: props => {
        const {
          delegate
        } = props;
        const key = `${OpenApiGroupDelegate.TYPE}:${bp.type}`;
        if (!conditionOptions[key]) {
          conditionOptions[key] = {
            logicalOperators: ['and'],
            attributeCondition: {
              attributeFilter: attr => {
                return attr?.source?.filterable;
              },
              comparatorFilter: plg => {
                return delegate.canConvertComparator(plg, {
                  ...props,
                  objectType: bp.type
                });
              }
            }
          };
        }
        return {
          creators,
          conditionOptions: conditionOptions[key]
        };
      }
    });
  });
  molten.addPlugin('Action', CreateAction(apiName, Object.keys(blueprints)));
  molten.addPlugin('Action', UpdateAction(apiName, Object.keys(blueprints)));
  molten.addPlugin('Action', DeleteAction(apiName, Object.keys(blueprints)));
  const paths = api.paths;
  if (paths) {
    const relationships = Object.entries(paths).map(([pathStr, methods]) => {
      return processRoute({
        path: pathStr,
        methods
      }, context);
    }).filter(rel => !!rel);
    relationships.forEach(rel => processRelationship(rel, context));
  }
}
export default {
  id: 'molten.openApiAnalyzer',
  sort: 1,
  init: async molten => {
    const apis = molten.getConfig('openApis');
    if (apis) {
      const nApis = await Promise.all(apis.map(api => OpenApiParser.validate(api)));
      nApis.forEach(api => processApi(api, {
        molten
      }));
    }
  }
};